<template>
    <v-container fluid>
        <div class="d-flex pb-1">
            <v-row>
                <v-col cols="auto">
                    <slot name="breadcrumbs" />
                </v-col>
                <!-- account -->
                <v-col lg="2" md="3" sm="4" cols="6" v-if="postingPattern">
                    <v-autocomplete
                        v-model="filterData.accountId"
                        :label="$t('inputs.account')"
                        :items="accounts.filter(c => c.type === type.sub) || []"
                        :item-text="c => `${c.accounting_no} - ${c.name}`"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                    />
                </v-col>
                <!-- from-date -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <bee-date-input
                        v-model="filterData.fromDate"
                        :label="$t('inputs.from-date')"
                        dense
                        hide-details
                        prepend-icon="mdi-calendar"
                        user-menu-picker
                        input-format-order="DD-MM-YYYY"
                    ></bee-date-input>
                </v-col>

                <!-- to-date -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <bee-date-input
                        v-model="filterData.toDate"
                        :label="$t('inputs.to-date')"
                        dense
                        hide-details
                        prepend-icon="mdi-calendar"
                        user-menu-picker
                        input-format-order="DD-MM-YYYY"
                    ></bee-date-input>
                </v-col>
            </v-row>
        </div>

        <v-form @submit.prevent="filter()">
            <v-row dense class="my-0">
                <!-- account -->
                <v-col lg="2" md="3" sm="4" cols="6" v-if="!postingPattern">
                    <v-autocomplete
                        v-model="filterData.accountId"
                        :label="$t('inputs.account')"
                        :items="accounts.filter(c => c.type === type.sub) || []"
                        :item-text="c => `${c.accounting_no} - ${c.name}`"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                    />
                </v-col>
                <!-- warehouse -->
                <v-col class="px-0" lg="2" md="3" sm="4" cols="6">
                    <v-autocomplete
                        v-model="warehouseItems"
                        multiple
                        clearable
                        :label="$t('inputs.warehouse')"
                        :items="isWarehouseKeeper ? warehousesForKeeper : warehouses"
                        :item-text="isWarehouseKeeper ? 'warehouse_name' : 'name'"
                        :item-value="isWarehouseKeeper ? 'warehouse_id' : 'id'"
                        dense
                        outlined
                        hide-details
                        @change="materialsId=[]"
                    >
                        <template v-slot:selection="{ item, index }">
                            <div v-if="index === 0">
                                <span>{{ item.name }}</span>
                            </div>
                            <span
                                v-if="index === 1"
                                class="grey--text text--darken-2 text-caption ms-2"
                                >
                                (+{{  warehouseItems.length - 1 }})
                            </span>
                        </template>
                    </v-autocomplete>
                </v-col>
                
                <!-- material -->
                <v-col lg="2" md="3" sm="4" cols="6">
                    <v-autocomplete
                        v-model="materialsId"
                        :label="$t('inputs.material')"
                        :items="materials.filter(c =>  warehouseItems.includes(c.warehouse_id))"
                        :item-text="c => `${c.no} - ${c.name}`"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        clearable
                        multiple
                    >
                        <template v-slot:item="data">
                            <v-list-item
                                :class="type.main === data.item.type ? 'orangeLighten5': 'none'"
                                v-on="data.on"
                                v-bind="data.attrs"
                            >
                                <v-list-item-action  class="me-4 ms-0">
                                    <v-checkbox :value="data.attrs.inputValue"></v-checkbox>
                                </v-list-item-action>
                                <div class="d-flex flex-column py-1">
                                    <span>
                                        {{ data.item.no + '-'+ data.item.name }}
                                    </span>
                                    <span v-if="warehouseItems.length > 1" class="blue-grey--text text--darken-1 text-body-2">
                                        {{ isWarehouseKeeper 
                                            ? warehousesForKeeper.find(c => data.item.warehouse_id === c.id)?.warehouse_name 
                                            : warehouses.find(c => data.item.warehouse_id === c.id)?.name  }}
                                    </span>
                                </div>
                                <v-spacer></v-spacer>
                                <bee-material-amount-label :with-color="true" :value="data.item.balance" :show-zeros-values="true"></bee-material-amount-label>

                            </v-list-item>
                        </template>
                        <template v-slot:selection="{ item, index }">
                            <div v-if="index === 0">
                                <span>{{ item.name }}</span>
                            </div>
                            <span
                                v-if="index === 1"
                                class="grey--text text--darken-2 text-caption ms-2"
                                >
                                (+{{  materialsId.length - 1 }})
                            </span>
                        </template>
                    </v-autocomplete>
                </v-col>
                <!--status-->
                <v-col cols="6" sm="4" md="2" lg="2" class="px-1" v-if="postingPattern && useSalesStatementInvoicesStatusFilter">
                    <v-autocomplete 
                        v-model="status"
                        :items="invoiceStatus"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        :label="$t('inputs.invoice-status')"
                        clearable
                    />
                </v-col>
                <v-col cols="12" sm="2" v-if="useMaterialStatusDeliver">
                    <v-autocomplete
                        v-model="deliveryStatuses"
                        :items="deliveryStatus"
                        item-value="id"
                        item-text="name"
                        outlined
                        dense
                        hide-details
                        :label="$t('delivery-status')"
                        multiple
                    >
                        <template v-slot:prepend-item>
                            <v-list-item
                                ripple
                                @mousedown.prevent
                                @click="toggle"
                            >
                                <v-list-item-action>
                                    <v-icon :color="deliveryStatuses.length > 0 ? 'indigo darken-4' : ''">
                                        {{ checkIcon }}
                                    </v-icon>
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ $t('select-all') }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-2"></v-divider>
                        </template>

                        <template v-slot:selection="{ item, index }">
                            <div v-if="index === 0">
                                <span>{{ item.name }}</span>
                            </div>
                            <span
                                v-if="index === 1"
                                class="grey--text text--darken-2 text-caption ms-2"
                                >
                                (+{{ deliveryStatuses.length - 1 }})
                            </span>
                        </template>
                    </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4" md="3" lg="2">
                    <v-text-field 
                    v-model="filterData.notes"
                    :label="$t('inputs.notes')"
                    outlined
                    dense
                    hide-details
                    />
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" sm="4" md="3" lg="2">
                    <v-row>
                        <v-spacer></v-spacer>
                        <v-col cols="auto" class="px-0">
                            <v-btn dark color="blueGray" @click="filter()" type="submit">
                                {{$t('update')}}
                            </v-btn>
                        </v-col>
                        <v-col cols="auto">
                            <v-menu offset-y>
                                <template v-slot:activator="{ attr, on }">
                                    <v-btn dark color="alert-color" v-on="on" v-bind="attr" class="me-1">
                                        <v-icon class="me-1">mdi-export-variant</v-icon>
                                        {{$t('exports.export')}}
                                    </v-btn>
                                </template>
        
                                <v-list nav dense>
                                    <v-list-item @click="getReport('pdf')">
                                        <v-list-item-icon class="me-4">
                                            <v-icon>mdi-printer</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>
                                            {{$t('exports.export-as-pdf')}}
                                        </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item @click="getReport('excel')">
                                        <v-list-item-icon class="me-4">
                                            <v-icon>mdi-microsoft-excel</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>
                                            {{$t('exports.export-as-excel')}}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-form>

        <!-- table -->
        <!-- {{ warehouses.find(c => c.id === materials.find(c => c.id === salesStatement.find(c => c.material_id === 16227).material_id).warehouse_id).name }} -->

        <bee-handy-table
        	:headers="headers"
            :items="loading ? [] : salesStatement"
            fixed-header
            :items-per-page="25"
            pagination-on-scroll
            :height="$vuetify.breakpoint.height - 225"
            :data-table-props="{ disableSort: true }"
            :loading="loading"
            zebra
            :shown-rows-text="$t('shown-rows-text')"
            :context-menu-items="contextMenuItems"
            @contextmenu:row="(e, { item }) => contextMenuDeclarations(item)"
            @context-menu-show-related-journal="showRelatedJournalPage(relatedId)"
            @context-menu-show-related-invoice="showRelatedInvoice(relatedInvoice)"
            @dblclick:row="(e, { item }) => {
                contextMenuDeclarations(item),
                entryHasInvoice ? showRelatedInvoice(relatedInvoice) : showRelatedJournalPage(relatedId)
            }"
        >
            <template v-slot:item.quantity="{item}">
                <span dir="ltr">
                    {{ item.quantity.quantity.toLocaleString() }}
                </span>
            </template>
            <template v-slot:item.price="{item}">
                <span dir="ltr">
                    {{ item.price.amount.toLocaleString() }}
                </span>
            </template>
            <template v-slot:item.total="{ item }">
                <span dir="ltr">
                    {{ (item.price.amount * item.quantity.quantity === -0 ? 0 : item.price.amount * item.quantity.quantity).toLocaleString() }}
                </span>
            </template>

            <template v-slot:item.is_posted="{ item }" v-if="postingPattern">
                <v-chip 
                    class="px-1"
                    :color="
                        item.posting_type === postingType.both
                            ?'postedColor' 
                            :item.posting_type === postingType.bond || item.posting_type === postingType.materialsJournal
                                ? 'yellow darken-4'
                                : 'unpostedColor'" 
                        dark
                >
                    <span class="mx-1 text-caption font-weight-bold">
                        {{
                            item.posting_type === postingType.both
                                ?$t('invoices.fully-posted')
                                : item.posting_type === postingType.bond
                                    ? $t('invoices.warehouse-non-posted') 
                                    : item.posting_type === postingType.materialsJournal
                                        ? $t('invoices.non-posted-for-accounting')
                                            :item.is_posted
                                                ? $t('posted')
                                                : $t('unposted')   
                        }}
                    </span>
                    <v-icon left dark small class="mx-1">{{ item.is_posted ? 'fa-envelope' : 'fa-envelope-open' }}</v-icon>
                </v-chip>
            </template>
            <template v-slot:pagination-on-scroll.page-text="{shownItemsLength, totalItems}" v-if="!this.isWarehousePermission">
                <div class="d-flex justify-space-between">
                    <span class="mx-10">
                    {{ $t('total-of-totals') }} {{ totalOfTotals.toLocaleString() }}
                    </span>

                    <span class="me-8">
                        {{shownItemsLength}} / {{totalItems}}
                    </span>
                </div>
            </template>
            <template v-slot:item.options="{ item }">
                <div class="text-center">
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attr }">
                            <v-btn
                                small
                                icon
                                v-bind="attr"
                                v-on="on"
                                width="32"
                                @click="contextMenuDeclarations(item)"
                            >
                                <v-icon>
                                    mdi-dots-vertical
                                </v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="showRelatedJournalPage(relatedId)">
                                <v-list-item-content class="pa-0">
                                    {{ $t('menus.show-related-journal') }}
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item v-if="entryHasInvoice" @click="showRelatedInvoice(relatedInvoice)">
                                <v-list-item-content class="pa-0">
                                    {{ $t('menus.show-related-invoice') }}
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </template>
        </bee-handy-table>
    </v-container>
</template>

<script>
import { useSalesStatement, useManufacturerCode, useMaterialStatusDeliver, showCategoryColumn, useSalesStatementInvoicesStatusFilter } from '@/configs/global'
import { mapGetters, mapState } from 'vuex';
import { transactionType, type, userType, invoiceType, postingPriorityType, postingType, invoicesPostingTypeStatuses } from '@/helpers/enums'
import ls from "@/settings/localStorageExtension";
import moment from 'moment';

export default {
    name: 'SalesStatement',

    data() {
        return {
            // helpers
            type,
            useSalesStatementInvoicesStatusFilter,
            loading: false,
            relatedId: null,
            materialsId: null,
            filterData: {
                accountId: null,
                fromDate: moment().subtract('months', 3).format('YYYY-MM-DD'),
                toDate: moment().format('YYYY-MM-DD'),
                notes: null
            },
            postingPriorityType,
            postingType,
            invoicesPostingTypeStatuses,
            warehouseItems: [],
            useManufacturerCode,
            showCategoryColumn,
            useMaterialStatusDeliver,
            showDeliveryStatus: false,
            deliveryStatuses: [],

            invoiceType,
            entryHasInvoice: false,
            status: null
        }
    },

    computed: {
        ...mapState({
            currentFinancialFile: state => state.financialFiles.current_financial_file,
            accounts: state => state.configs.configs.accounts,
            materials: state => state.materials.materials,
            salesStatement: state => state.statements.salesStatement,
            userTypePermission: state => state.permissions.user_type,
            warehouses: state => state.warehouses.warehouses,
            deliveryStatus: state => state.deliveryStatus.deliveryStatus,
            warehousesForKeeper: state => state.permissions.warehousesForKeeper,
        }),
        
        ...mapGetters({
            getAccountById: 'configs/getAccountById',
            getMaterialById: 'materials/getMaterialById',
            getSettingByKey: 'settings/getSettingByKey'
        }),

        headers() {
           
            let headers = [
                useManufacturerCode ? 
                    {
                        text: this.$t('headers.manufacturer-code'),
                        name: 'material',
                        value: 'soft_material_info.manufacturer_code',
                    }
                    : null,
                {
                    text: this.$t('inputs.warehouse'),
                    name: 'warehouse',
                    value: 'soft_material_info.warehouse_name',
                },
                {
                    text: this.$t('headers.material'),
                    name: 'material',
                    value: 'soft_material_info.name',
                },
                showCategoryColumn
                ? {
                    text: this.$t('headers.category'),
                    name: 'parent_material',
                    value: 'soft_material_info.parent_material_arabic_name',
                    cellClass: 'text-no-wrap'
                }
                :null,
                {
                    text: this.$t('headers.quantity'),
                    name: 'quantity',
                    value: 'quantity.quantity',
                },
                
                ...(
                    !this.isWarehousePermission
                        ? [
                            {
                                text: this.$t('headers.price'),
                                name: 'price',
                                value: 'price.amount',
                            },
                            {
                                text: this.$t('headers.total'),
                                name: 'total',
                                value: 'total',
                            }
                        ]
                        : []
                ),
                
                
                this.showDeliveryStatus ?
                    {
                        text: this.$t('InvoiceBuyPage.headers.delivery-status'),
                        value: 'delivery_status_id',
                        name: 'delivery_status_id',
                        noNotAvailableText: true,
                        setValueExpr: val => this.deliveryStatus.find(c => c.id === val)?.name
                    }
                    : null,
                
                {
                    text: this.$t('headers.account'),
                    name: 'account',
                    value: 'client_id',
                    setValueExpr: val => this.getAccountById(val)?.name
                },
                this.postingPattern && useSalesStatementInvoicesStatusFilter?
                {
                    text: this.$t('headers.state'),
                    name: 'is_posted',
                    value: this.postingPattern? 'posting_type':'is_posted',
                    sortable: false
                }
                :null,
                {
                    text: this.$t('headers.ref-doc'),
                    name: 'refDoc',
                    value: 'doc_no',
                },
                {
                    text: this.$t('headers.invoice-number'),
                    name: 'invoiceNumber',
                    value: 'invoice_no',
                    type: 'number', 
                },
                {
                    text: this.$t('headers.notes'),
                    name: 'notes',
                    value: 'notes', 
                    type: 'string', 
                },
                {
                    text: this.$t('headers.date'),
                    name: 'date',
                    value: 'date', 
                    type: 'date', 
                },
                {
                    text: '',
                    name: 'options',
                    value: 'options',
                    width: 34
                }
            ];

            headers = headers.filter(c => Boolean(c))
            
            for (let i = 0; i < headers.length; i++) {
                if(i === headers.length - 1) {
                    headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-0' : 'px-0';
                } else {
                    headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
                }
            }
            return headers
        },
        
        contextMenuItems() {
            const contextMenuItems = [
                {
                    title: this.$t('menus.show-related-journal'),
                    event: 'show-related-journal'
                },
            ];
            if(this.entryHasInvoice) {
                contextMenuItems.push({
                    title: this.$t('menus.show-related-invoice'),
                    event: 'show-related-invoice'
                })
            }

            return contextMenuItems
        },

        isWarehousePermission() {
			return (this.userTypePermission === userType.warehouseKeeper) || (this.userTypePermission === userType.warehouseManger)
    	},
        isWarehouseKeeper() {
            return this.userTypePermission === this.warehouseKeeper;
        },
        selectedAllDeliveryStatuses () {
            return this.deliveryStatuses.length === this.deliveryStatus.length
        },

        selectedPartDeliveryStatuses () {
            return this.deliveryStatuses.length > 0 && !this.selectedAllDeliveryStatuses
        },

        checkIcon () {
            if (this.selectedAllDeliveryStatuses) return 'mdi-close-box'
            if (this.selectedPartDeliveryStatuses) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        },

        totalOfTotals() {
            return this.salesStatement.reduce((sum, item) => {
                return sum + item.price.amount * item.quantity.quantity;
            }, 0);
        },
        materialsRequest() {
            return this.materialsId?.map(t => {
                const material = this.getMaterialById(t);
                return {
                    material_id: t,
                    is_parent: material?.type === type.main,
                    warehouse_id: material.warehouse_id
                }
            }
            )
        },
        invoiceStatus() {
            let status = invoicesPostingTypeStatuses;
            status.unshift({
                name: this.$t('all'),
                id: null
            })
            return status
        },
        postingPattern() {
            return this.getSettingByKey('Posting Pattern').setting_value !== postingPriorityType.both;
        },
    },

    methods: {
        showRelatedJournalPage(relatedId) {
            const route = this.$router.resolve({ name: 'materials-journal', params: { id: relatedId } });
                window.open(route.href, '_blank');
        },
        showRelatedInvoice(relatedInvoice) {
            const route = this.$router.resolve({
                name: 
                    relatedInvoice.invoiceType === this.invoiceType.buy ? 'invoices-buy' 
                    : relatedInvoice.invoiceType === this.invoiceType.sale ? 'invoices-sale' 
                    : relatedInvoice.invoiceType === this.invoiceType.buyRefund ? 'buy-invoice-refund' 
                    : relatedInvoice.invoiceType === this.invoiceType.saleRefund ? 'sale-invoice-refund' 
                    : null,

                params: { id: relatedInvoice.invoiceId },
                query: { withCategory: true }
            });
                window.open(route.href, '_blank');
        },
        filter(isFirstLoad) {
            this.deliveryStatuses.length > 0 ? this.showDeliveryStatus = true : this.showDeliveryStatus = false;
            this.loading = true;
            this.$store.dispatch('statements/fetchSalesStatements', {
                materialsRequest: this.materialsRequest,
                clientId: this.filterData.accountId,
                fromDate: this.filterData.fromDate,
                toDate: this.filterData.toDate,
                notes: this.filterData.notes,
                /*the 0 value mean that i want only the posted invoices*/
                postingTypeAdvance: useSalesStatementInvoicesStatusFilter ? this.status: 0,
                transactionType: transactionType.out, // out,
                deliveryStatusIds: this.deliveryStatuses || [],
            }).finally(() => {
                if (!isFirstLoad) this.loading = false;
            })
        },

        getReport(type) {
            this.$store.dispatch('reports/salesStatementReport', {
                type,
                financialFileId: this.currentFinancialFile.id,
                userId: ls.getLocalUser().id,
                materialRequests: JSON.stringify(this.materialsRequest),
                clientId: this.filterData.accountId,
                fromDate: this.filterData.fromDate,
                toDate: this.filterData.toDate,
                notes: this.filterData.notes,
                transactionType: transactionType.out,
                postingTypeAdvance: useSalesStatementInvoicesStatusFilter ? this.status: 0,
                deliveryStatusIds: this.deliveryStatuses.length > 0 ? this.deliveryStatuses : null,
                withManufacturerCode: this.useManufacturerCode
            })
        },

        toggle () {
            this.$nextTick(() => {
                if (this.selectedAllDeliveryStatuses) {
                    this.deliveryStatuses = []
                } else {
                    this.deliveryStatuses = this.deliveryStatus.slice()
                    this.deliveryStatuses = this.deliveryStatus.map(c => c.id)
                }
            })
        },

        contextMenuDeclarations(item) {
            this.entryHasInvoice = Boolean(item.invoice_id)
            this.relatedId = item.materials_journal_id, 
            this.relatedInvoice = { invoiceId: item.invoice_id, invoiceType: item.invoice_type } 
        }
    },

    created() {
        this.loading = true;
        Promise.all([
            !this.materials.length ? this.$store.dispatch('materials/fetchAll') : null,
            this.filter(),
        ]).finally(() => {
            this.loading = false;
        });
        this.$store.dispatch('warehouses/fetchAll').then(() => {
            this.warehouseItems.push(this.warehouses.find(c => c.is_default === true).id)
        })
        !(this.deliveryStatus.length > 0) ? this.$store.dispatch('deliveryStatus/fetchAll') : null
    },

    beforeRouteEnter(to, from, next) {
        if (useSalesStatement) next(true);
        else next({ name: 'home' });
    }
}
</script>

<style>

</style>